import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Constants from '../../components/api/constants'

export default function AppNotificationList(props) {

  const { data, onEdit, loading, refreshData } = props;

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [selectedName, setSelectedName] = useState('');
  const [selectedActive, setSelectedActive] = useState(true);
  const [openResend, setOpenResend] = useState(false);

  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleCloseResendDialog = () => setOpenResend(false);

  const handleConfirmResend = async () => {
    setOpenResend(false);
    await handleResend(selectedId, selectedName);
  };

  useEffect(() => {
    if (!data) return;

    const mapped = data.map(n => ({
      id: n.id,
      name: n.name,

      englishTitle: n.englishTitle,
      englishMessage: n.englishMessage,
      germanTitle: n.germanTitle,
      germanMessage: n.germanMessage,
      spanishTitle: n.spanishTitle,
      spanishMessage: n.spanishMessage,
      frenchTitle: n.frenchTitle,
      frenchMessage: n.frenchMessage,
      dutchTitle: n.dutchTitle,
      dutchMessage: n.dutchMessage,

      createdDateTime: n.createdDateTime,
      status: n.isActive,

      edit: (
        <Button
          variant="contained"
          size="small"
          onClick={() => onEdit(n)}
        >
          Edit
        </Button>
      ),

      resend: (
        <Button
          variant="contained"
          color="info"
          size="small"
          onClick={() => {
            setSelectedId(n.id);
            setSelectedName(n.name);
            setOpenResend(true);
          }}
        >
          Resend
        </Button>
      ),

      disable: (
        <Button
          variant="contained"
          color={n.isActive ? "error" : "success"}
          size="small"
          onClick={() => handleOpenDialog(n.id, n.name, n.isActive)}
        >
          {n.isActive ? "Disable" : "Enable"}
        </Button>
      )
    }));

    setRows(mapped);
  }, [data]);

  const handleOpenDialog = (id, name, isActive) => {
    setSelectedId(id);
    setSelectedName(name);
    setSelectedActive(isActive);
    setOpen(true);
  };

  const handleCloseDialog = () => setOpen(false);

  const handleConfirmAppNotificationStatus = async () => {
    setOpen(false);

    try {
      const newStatus = !selectedActive;

      const requestOptions = {
        method: 'POST',
        credentials: "include"
      };

      const parameters = new URLSearchParams({
        id: selectedId,
        isActive: newStatus
      });

      const response = await fetch(
        Constants.baseURL + Constants.apiSetAppNotificationStatus + '?' + parameters,
        requestOptions
      );

      if (!response.ok) {
        throw new Error('API failed');
      }

      toast.success(
        selectedActive ? 'Notification disabled' : 'Notification enabled',
        {
          position: "top-right",
          autoClose: 3000
        }
      );

      if (refreshData) {
        await refreshData();
      }

    } catch (err) {
      toast.error('Failed to update notification', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleResend = async (id, name) => {
    try {
      const requestOptions = {
        method: 'DELETE',
        credentials: "include"
      };

      const parameters = new URLSearchParams({
        id: id
      });

      const response = await fetch(
        Constants.baseURL + Constants.apiResetAppNotification + '?' + parameters,
        requestOptions
      );

      if (!response.ok) {
        throw new Error('API failed');
      }

      toast.success('Notification resent', {
        position: "top-right",
        autoClose: 3000
      });

      if (refreshData) {
        await refreshData();
      }

    } catch (err) {
      toast.error('Failed to resend notification', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const renderMultiLang = (row, type, isExpanded) => {
    const safe = (v) => (v === null || v === undefined ? '' : v);

    const items = [
      { code: 'au', label: 'EN', title: safe(row?.englishTitle), message: safe(row?.englishMessage) },
      { code: 'de', label: 'DE', title: safe(row?.germanTitle), message: safe(row?.germanMessage) },
      { code: 'es', label: 'ES', title: safe(row?.spanishTitle), message: safe(row?.spanishMessage) },
      { code: 'fr', label: 'FR', title: safe(row?.frenchTitle), message: safe(row?.frenchMessage) },
      { code: 'nl', label: 'NL', title: safe(row?.dutchTitle), message: safe(row?.dutchMessage) },
    ];

    const visibleItems = isExpanded ? items : items.slice(0, 1);

    return (
      <div style={{ lineHeight: '1.4em' }}>
        {visibleItems.map((x, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'flex-start',
              marginBottom: 6
            }}
          >
            <img
              src={`https://flagcdn.com/w20/${x.code}.png`}
              alt={x.label}
              style={{
                width: 20,
                height: 14,
                marginTop: 3,
                borderRadius: 2
              }}
            />

            <div>
              <b>{x.label}:</b>{' '}
              {type === 'title'
                ? (x.title || '')
                : (x.message || '')}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      field: 'expand',
      headerName: '',
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      renderCell: (params) => {
        const isExpanded = expandedRows.has(params.id);

        return (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(params.id);
            }}
          >
            {isExpanded
              ? <KeyboardArrowUpIcon sx={{ color: 'white' }} />
              : <KeyboardArrowDownIcon sx={{ color: 'white' }} />
            }
          </Button>
        );
      }
    },

    { field: 'id', headerName: 'ID', minWidth: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },

    {
      field: 'title',
      headerName: 'Title',
      flex: 2,
      minWidth: 320,
      renderCell: (params) =>
        renderMultiLang(params.row, 'title', expandedRows.has(params.id))
    },

    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 320,
      renderCell: (params) =>
        renderMultiLang(params.row, 'message', expandedRows.has(params.id))
    },

    {
      field: 'createdDateTime',
      headerName: 'Sent',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        new Date(params.value + 'Z').toLocaleDateString('en-AU', {
          timeZone: 'Australia/Sydney'
        }) + ' AEST'
    },

    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => {
        const isActive = params.value === true;

        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "error"}
            size="small"
          />
        );
      }
    },

    {
      field: 'edit',
      headerName: 'Edit',
      minWidth: 120,
      sortable: false,
      renderCell: (params) => params.value
    },

    {
      field: 'resend',
      headerName: 'Resend',
      minWidth: 140,
      sortable: false,
      renderCell: (params) => params.value
    },

    {
      field: 'disable',
      headerName: 'Disable',
      minWidth: 140,
      sortable: false,
      renderCell: (params) => params.value
    }
  ];

  return (
    <React.Fragment>

      <ToastContainer />

      <div style={{ height: 650, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          disableSelectionOnClick
          loading={loading}

          onRowClick={(params) => toggleRow(params.id)}

          getRowHeight={(params) =>
            expandedRows.has(params.id) ? 140 : 60
          }

          sx={{
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none !important",
            },
          }}
        />
      </div>

      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>
          {selectedActive ? "Disable Notification" : "Enable Notification"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedActive ? "disable" : "enable"}: <b>{selectedName}</b>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            color={selectedActive ? "error" : "success"}
            onClick={handleConfirmAppNotificationStatus}
          >
            {selectedActive ? "Disable" : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openResend} onClose={handleCloseResendDialog}>
        <DialogTitle>
          Resend Notification
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to resend: <b>{selectedName}</b>?
            <br />
            This will make it visible again for all users.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseResendDialog}>Cancel</Button>
          <Button
            color="info"
            onClick={handleConfirmResend}
          >
            Resend
          </Button>
        </DialogActions>
      </Dialog>

    </React.Fragment>
  );
}